import { useEffect, useRef } from 'react';

const features = [
  { icon: 'ri-file-chart-line', text: '歯並び・骨格の診断結果' },
  { icon: 'ri-scales-3-line', text: '治療法ごとの比較（マウスピース／ワイヤー）' },
  { icon: 'ri-time-line', text: '期間・費用の見通し' },
  { icon: 'ri-emotion-line', text: '生活習慣や性格との相性' },
];

export default function MouthpieceKeyMessage() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-up, .scale-in, .fade-up-item').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 80);
            });
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -30px 0px' }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-20 md:py-24 bg-stone-50/60 overflow-hidden">
      {/* 上部の細いグラデーションライン — Hero との境界 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-stone-200 to-transparent" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* バッジ */}
        <div className="text-center mb-8 fade-up">
          <span
            className="inline-block text-white px-5 py-1.5 rounded-full text-sm font-medium tracking-wider"
            style={{ backgroundColor: '#C4A265' }}
          >
            無料
          </span>
        </div>

        {/* メインタイトル */}
        <h2
          className="fade-up text-2xl md:text-3xl lg:text-4xl font-normal text-center text-gray-900 mb-4 leading-snug font-serif"
        >
          一般的な無料カウンセリングでは得られない
        </h2>

        <h3
          className="fade-up text-3xl md:text-4xl lg:text-5xl font-normal text-center text-gray-900 mb-6 leading-snug"
          style={{ fontFamily: 'Noto Serif JP, serif' }}
        >
          《あなた専用矯正プラン<br className="sm:hidden" />比較レポート》を<br className="hidden sm:block" />
          無料提供します
        </h3>

        <p className="fade-up text-center text-gray-600 text-base md:text-lg mb-10 leading-relaxed max-w-2xl mx-auto">
          他院では有料級（通常３～５万円相当）の情報を、当院ではすべて無料でご提供。<br />
          あなたが納得して治療法を選べるよう、判断材料を整理してお渡しします。
        </p>

        {/* レポート画像 + 特徴 の2カラム */}
        <div className="flex flex-col md:flex-row items-center gap-10 md:gap-14 mb-12">
          {/* レポート画像 */}
          <div className="scale-in flex-shrink-0 w-full md:w-[340px]">
            <div className="relative mx-auto max-w-[260px] md:max-w-full">
              <img
                alt="矯正プラン比較レポート"
                className="w-full h-auto rounded-lg"
                src="https://storage.googleapis.com/studio-design-asset-files/projects/Kwa5AGrYaX/s-1649x1878_v-frms_webp_bfc64f58-af11-440b-b5a7-e0dc193232f2_small.webp"
              />
              {/* レポートの周りにさりげない背景アクセント */}
              <div
                className="absolute -inset-3 -z-10 rounded-2xl opacity-30"
                style={{ backgroundColor: '#F5F0E8' }}
              />
            </div>
          </div>

          {/* 特徴リスト */}
          <div className="flex-1 w-full">
            <p className="fade-up text-sm text-gray-400 tracking-wider mb-5 text-center md:text-left">
              このレポートでわかること
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="fade-up-item flex items-center gap-3 bg-white rounded-xl px-5 py-4"
                >
                  <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-full" style={{ backgroundColor: '#EEF3F9' }}>
                    <i className={`${feature.icon} text-base`} style={{ color: '#3E5F8F' }} />
                  </div>
                  <p className="text-sm text-gray-700 leading-snug">{feature.text}</p>
                </div>
              ))}
            </div>

            {/* 補足メッセージ */}
            <p className="fade-up text-sm text-gray-500 mt-5 leading-relaxed text-center md:text-left">
              「なんとなく」ではなく、<strong className="text-gray-700">根拠をもとに</strong>治療法を比較し、選べるようになります。
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="fade-up text-center">
          <a
            href="https://reservation.stransa.co.jp/8878b04a60c23f1ce5b3268e265263cb"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-white px-10 py-4 rounded-lg text-lg font-semibold transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
            style={{ backgroundColor: '#3E5F8F' }}
          >
            無料診断を予約する（レポート作成）
            <i className="ri-arrow-right-line" />
          </a>
          <p className="text-sm text-gray-400 mt-4">
            費用が発生するのは、治療を希望され契約する場合のみです
          </p>
        </div>
      </div>
    </section>
  );
}